
<script setup>
import { ref, computed, watch } from 'vue'
import { useTranslaterStore } from '@/stores/translater'
import { useCertificatesStore } from '@/stores/certificates'
import checkValid from '@/helpers/checkValid'
import calcDatas from '@/helpers/calcDatas'

const props = defineProps({
    certificate: {
        type: Object,
        required: false
    }, 
    change: {
        type: Boolean
    }
})

const translaterStore   = useTranslaterStore()
const certificatesStore = useCertificatesStore()

const ADTCertificatesCreateStart    = ref(calcDatas('now'))
const ADTCertificatesCreateEnd      = ref(calcDatas('century') )
const ADTCertificatesIMEI           = ref(null)
const formInvalids                  = ref({})
const formErrors                    = ref([])

const getTranslate              = computed(() => translaterStore.getTranslate)
const certificatesCreateProcess = computed(() => certificatesStore.certificatesCreateProcess)
const certificatesCreateMsg     = computed(() => certificatesStore.certificatesCreateMsg)

function checkIMEI(e) {
    if (checkValid(e.target.value, 'imei')) {
        e.target.classList.remove('uta-input--invalid')
        formInvalids.value.ADTCreateCarIMEI = false
    }
}

function submitForm() {

    formInvalids.value = {}
    formErrors.value = []

    const data = {
        start: new Date(ADTCertificatesCreateStart.value).getTime() / 1000,
        stop: new Date(ADTCertificatesCreateEnd.value).getTime() / 1000
    }

    console.log(ADTCertificatesIMEI.value, checkValid(ADTCertificatesIMEI.value, 'imei'))

    if(props.certificate) {
        data.imei = props.certificate.imei
    } else {
        if(ADTCertificatesIMEI.value && checkValid(ADTCertificatesIMEI.value, 'imei')) {
            data.imei = ADTCertificatesIMEI.value
        } else {
            formInvalids.value.ADTCertificatesIMEI = true
            formErrors.value.push(getTranslate.value['CERTIFICATES_IMEI'])
        }
    }

    if(formErrors.value.length == 0) {
        props.certificate ? 
            certificatesStore.updateCertificates(data) :
            certificatesStore.createCertificates(data)
    }
}

if(props.certificate) {
    ADTCertificatesCreateStart.value   = calcDatas('tm', props.certificate.start * 1000)
    ADTCertificatesCreateEnd.value     = calcDatas('tm', props.certificate.stop * 1000)
}

watch(ADTCertificatesCreateStart, date => {
    ADTCertificatesCreateEnd.value = calcDatas('century', date) 
})

</script>

<template>
    <form action="" @submit.prevent="submitForm()" class="uta-certificate-create">

        <div 
            v-if="formErrors.length"
            class="mb-4 uta-alert uta-alert--danger text-start mb-3"
        >
            <span class="material-icons uta-alert__icon">error_outline</span>
            <div class="uta-alert__text">
                {{ getTranslate.EFormFields }}:
                <span class="uta-alert__text-item" v-for="(error, index) in formErrors" :key="error">
                    {{ error }}{{ index !== formErrors.length - 1 ? ', ' : '' }} 
                </span>
            </div>
        </div>

        <div 
            v-if="'success' in certificatesCreateMsg && !certificatesCreateMsg.success"
            class="mb-4 uta-alert uta-alert--danger"
        >
            <span class="material-icons uta-alert__icon">error_outline</span>
            <div class="uta-alert__text">
                {{ getTranslate[certificatesCreateMsg.text] || certificatesCreateMsg.text }}
            </div>
            <button type="button" class="uta-alert__close" @click="certificatesStore.setCertificatesCreateMsg({})">
                <span class="material-icons">close</span>
            </button>
        </div>

        <div class="row">
            <div class="my-2 col-12 col-md-6">
                <label for="ADTCertificatesCreateStart">{{ getTranslate.CERTIFICATES_DATE_START }}</label>
                <input 
                    class="uta-input uta-input--block" 
                    type="datetime-local" 
                    id="ADTCertificatesCreateStart"
                    v-model="ADTCertificatesCreateStart"
                >
            </div>
            <div class="my-2 col-12 col-md-6">
                <label for="ADTCertificatesCreateEnd">{{ getTranslate.CERTIFICATES_DATE_STOP }}</label>
                <input 
                    class="uta-input uta-input--block" 
                    type="datetime-local" 
                    id="ADTCertificatesCreateStart"
                    v-model="ADTCertificatesCreateEnd"
                >
            </div>
        </div>

        <div class="my-2" v-if="!certificate">
            <label for="ADTCertificatesIMEI">{{ getTranslate.CERTIFICATES_IMEI }}</label>
            <input 
                class="uta-input uta-input--block" 
                :class="{ 'uta-input--invalid' : formInvalids.ADTCertificatesIMEI }"
                id="ADTCertificatesIMEI" 
                type="text" 
                v-model="ADTCertificatesIMEI"
                @input="$event.target.value = $event.target.value.length <= 15 ? $event.target.value : $event.target.value.substring(0, 15)"
                @keyup="checkIMEI"
            >
        </div>

        <div class="my-4" v-else>
            <label for="ADTCertificatesIMEI">{{ getTranslate.CERTIFICATES_IMEI }}</label>
            <input 
                class="uta-input uta-input--block" 
                id="ADTCertificatesIMEI" 
                type="text" 
                :value="certificate.imei"
                readonly
            >
        </div>

        <div v-if="certificate" class="mt-4">
            <strong>{{ getTranslate.CERTIFICATES_CHANGE_WARN }}</strong>
        </div>

        <div class="mt-4 uta-btns">
            <button type="submit" class="uta-btn uta-btn--main">
                <template v-if="!certificatesCreateProcess">
                    {{ certificate ? getTranslate.BTN_REISSUE : getTranslate.BTN_CREATE }}
                </template>
                <template v-else>
                    <div class="spinner-border" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                </template>
            </button>
        </div>

    </form>
</template>
